import React, { Suspense, lazy, useLayoutEffect, useState, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PowerHourGlow from './PowerHourGlow';

const AppNavbar = lazy(() => import('./AppNavBar'));
const AppFooter = lazy(() => import('./AppFooter'));

// Minimum time to show loading indicator (ms)
const MIN_LOADING_TIME = 500;

const Layout: React.FC = () => {
  const { pathname } = useLocation();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const isFirstRender = useRef(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle page transitions with minimum loading time
  useLayoutEffect(() => {
    // Skip first render - page loads normally
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Cancel any pending reveal from previous navigation
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Start transition - show loading indicator
    setIsTransitioning(true);

    // Instant scroll to top
    window.scrollTo(0, 0);

    // Reveal content after minimum loading time
    timeoutRef.current = setTimeout(() => {
      setIsTransitioning(false);
    }, MIN_LOADING_TIME);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [pathname]);

  // Loading indicator component
  const LoadingIndicator = () => (
    <div
      className="d-flex justify-content-center align-items-center"
      style={{ minHeight: '50vh' }}
    >
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );

  return (
    <div className="d-flex flex-column" style={{ minHeight: '100vh' }}>
      <Helmet defaultTitle="Project for Awesome" />
      <PowerHourGlow />
      <Suspense fallback={<div>Loading...</div>}>
        <AppNavbar />
      </Suspense>

      {/* Preview mode banner */}
      {(() => {
        try {
          const enabled = sessionStorage.getItem('p4a_preview_enabled') === '1' || new URLSearchParams(window.location.search).get('preview') === '1';
          if (!enabled) return null;
        } catch { return null; }
        const disable = () => {
          try {
            sessionStorage.removeItem('p4a_preview_enabled');
            sessionStorage.removeItem('p4a_preview_target');
            sessionStorage.removeItem('p4a_preview_path');
          } catch { }
          try {
            const url = new URL(window.location.href);
            url.searchParams.delete('preview');
            window.location.href = url.pathname + (url.search ? url.search : '');
          } catch {
            window.location.reload();
          }
        };
        return (
          <div className="bg-warning text-dark" style={{ padding: '6px 12px', fontSize: 14 }}>
            Preview mode active — fetching drafts from Strapi. <button className="btn btn-sm btn-dark ms-2" onClick={disable}>Disable</button>
          </div>
        );
      })()}

      <div className="bg-theme-background flex-grow-1 page-content">
        {isTransitioning ? <LoadingIndicator /> : <Outlet />}
      </div>
      <Suspense fallback={<div>Loading...</div>}>
        <AppFooter />
      </Suspense>
    </div>
  );
};

export default Layout;

